<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import CapiSettingsAPI from '../api';
import {
  FEP_TOTAL_MS,
  RESPONSE_TOTAL_MS,
  formatCountdown,
  progressRatio,
  remainingMs,
} from '../fepHelpers';

import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();

const fep = ref(null);
const now = ref(Date.now());
let ticker = null;
let refresher = null;

const fetchStatus = async () => {
  try {
    const { data } = await CapiSettingsAPI.conversationStatus(
      props.conversationId
    );
    fep.value = data.fep || null;
  } catch {
    fep.value = null;
  }
};

onMounted(() => {
  fetchStatus();
  ticker = setInterval(() => {
    now.value = Date.now();
  }, 1000);
  // status can flip (agent replied / window expired server-side); refresh cheaply
  refresher = setInterval(fetchStatus, 60_000);
});
onBeforeUnmount(() => {
  clearInterval(ticker);
  clearInterval(refresher);
});
watch(() => props.conversationId, fetchStatus);

const status = computed(() => fep.value?.status);
const deadlineMs = computed(() =>
  remainingMs(fep.value?.response_deadline_at, now.value)
);
const windowMs = computed(() => remainingMs(fep.value?.expires_at, now.value));

// local transition without waiting for the next refresh
const effectiveStatus = computed(() => {
  if (status.value === 'awaiting_reply' && deadlineMs.value === 0)
    return 'missed';
  if (status.value === 'open' && windowMs.value === 0) return 'expired';
  return status.value;
});

const visible = computed(() =>
  ['awaiting_reply', 'open', 'expired', 'missed'].includes(
    effectiveStatus.value
  )
);

const ratio = computed(() =>
  effectiveStatus.value === 'awaiting_reply'
    ? progressRatio(deadlineMs.value, RESPONSE_TOTAL_MS)
    : progressRatio(windowMs.value, FEP_TOTAL_MS)
);

const STYLES = {
  awaiting_reply: {
    icon: 'i-lucide-alarm-clock',
    box: 'bg-n-amber-3/60 text-n-amber-11',
    bar: 'bg-n-amber-9',
  },
  open: {
    icon: 'i-lucide-badge-dollar-sign',
    box: 'bg-n-teal-3/60 text-n-teal-11',
    bar: 'bg-n-teal-9',
  },
  expired: {
    icon: 'i-lucide-clock-alert',
    box: 'bg-n-alpha-1 text-n-slate-11',
    bar: 'bg-n-slate-8',
  },
  missed: {
    icon: 'i-lucide-clock-alert',
    box: 'bg-n-alpha-1 text-n-slate-11',
    bar: 'bg-n-slate-8',
  },
};
const style = computed(() => STYLES[effectiveStatus.value] || STYLES.expired);

const label = computed(() => {
  switch (effectiveStatus.value) {
    case 'awaiting_reply':
      return t('WASAPI_FEP.AWAITING', {
        time: formatCountdown(deadlineMs.value),
      });
    case 'open':
      return t('WASAPI_FEP.OPEN', { time: formatCountdown(windowMs.value) });
    case 'expired':
      return t('WASAPI_FEP.EXPIRED');
    default:
      return t('WASAPI_FEP.MISSED');
  }
});

const tooltip = computed(() => ({
  content: t('WASAPI_FEP.TOOLTIP'),
  popperClass: 'max-w-72',
}));
</script>

<template>
  <div
    v-if="visible"
    v-tooltip.top="tooltip"
    class="flex flex-col gap-1 px-2 py-1.5 mt-2 rounded-lg text-xs"
    :class="style.box"
    data-testid="fep-countdown"
  >
    <span class="flex items-center gap-1.5 font-medium tabular-nums">
      <Icon :icon="style.icon" class="size-3.5 shrink-0" />
      {{ label }}
    </span>
    <div
      v-if="['awaiting_reply', 'open'].includes(effectiveStatus)"
      class="h-1 rounded-full bg-n-alpha-2 overflow-hidden"
    >
      <div
        class="h-full rounded-full transition-[width] duration-1000 ease-linear"
        :class="style.bar"
        :style="{ width: `${ratio * 100}%` }"
      />
    </div>
  </div>
</template>
